import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, fmtDate, LEVEL_LABELS } from "@/lib/api";
import { statusChip } from "@/components/StatusChip";
import { Plus, Mic } from "lucide-react";

export default function Home() {
    const nav = useNavigate();
    const [items, setItems] = useState(null);

    useEffect(() => {
        api.get("/complaints").then((r) => setItems(r.data.items)).catch(() => setItems([]));
    }, []);

    return (
        <Layout>
            <div className="flex items-end justify-between mb-6">
                <div>
                    <h1 className="display text-[28px] md:text-[34px]">Your complaints</h1>
                    <p className="text-[var(--speakup-text-muted)] mt-1">
                        Speak the issue. We do the rest — drafting, routing, timing.
                    </p>
                </div>
                <button
                    onClick={() => nav("/new")}
                    data-testid="home-raise-complaint-btn"
                    className="hidden md:inline-flex items-center gap-2 h-12 px-5 rounded-[12px] text-white font-semibold"
                    style={{ background: "var(--speakup-red)" }}
                >
                    <Plus size={18} /> Raise a complaint
                </button>
            </div>

            {items === null && (
                <div className="speakup-card p-6 animate-pulse text-[var(--speakup-text-muted)]">Loading…</div>
            )}

            {items?.length === 0 && (
                <div className="speakup-card p-7 md:p-10 text-center" data-testid="home-empty-state">
                    <div
                        className="mx-auto mb-5 w-16 h-16 rounded-full flex items-center justify-center text-white"
                        style={{ background: "var(--speakup-gradient)" }}
                    >
                        <Mic size={28} />
                    </div>
                    <h2 className="display text-[22px]">No complaints yet</h2>
                    <p className="text-[var(--speakup-text-muted)] mt-2 max-w-md mx-auto">
                        Tap the button and tell us, in your own language, what went wrong with your bank, NBFC or
                        card. We'll draft the formal complaint for you — you'll just press send.
                    </p>
                    <button
                        onClick={() => nav("/new")}
                        data-testid="home-empty-raise-btn"
                        className="mt-6 inline-flex items-center gap-2 h-12 px-6 rounded-[12px] text-white font-semibold"
                        style={{ background: "var(--speakup-red)" }}
                    >
                        <Mic size={18} /> Raise your first complaint
                    </button>
                </div>
            )}

            {items?.length > 0 && (
                <ul className="space-y-3" data-testid="home-complaints-list">
                    {items.map((c) => {
                        const summary =
                            c.structured_payload?.summary ||
                            c.structured_payload?.relief_sought ||
                            "Grievance";
                        const entity =
                            c.entity_name_extracted ||
                            c.structured_payload?.entity_name ||
                            "Unknown institution";
                        const nextAction = nextActionLabel(c);
                        return (
                            <li key={c.id} data-testid={`complaint-card-${c.id}`}>
                                <Link
                                    to={`/c/${c.id}`}
                                    className="speakup-card p-5 block hover:shadow-md transition-shadow"
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <div className="text-[12px] uppercase tracking-wider text-[var(--speakup-text-muted)]">
                                                {entity} · {LEVEL_LABELS[c.current_level]}
                                            </div>
                                            <div className="display text-[18px] mt-1 line-clamp-2 leading-snug">
                                                {summary}
                                            </div>
                                            <div className="text-[13px] text-[var(--speakup-text-muted)] mt-2 tabular">
                                                {fmtDate(c.created_at)}
                                            </div>
                                        </div>
                                        <div className="shrink-0">{statusChip(c.status)}</div>
                                    </div>
                                    {nextAction && (
                                        <div className="mt-3 pt-3 border-t border-[var(--speakup-border)] text-[14px] text-[var(--speakup-text-strong)]">
                                            Next: <span className="font-semibold">{nextAction}</span>
                                        </div>
                                    )}
                                </Link>
                            </li>
                        );
                    })}
                </ul>
            )}

            {/* Mobile floating primary action */}
            <button
                onClick={() => nav("/new")}
                data-testid="home-fab-raise"
                className="md:hidden fixed bottom-6 right-5 h-14 w-14 rounded-full shadow-lg flex items-center justify-center text-white"
                style={{ background: "var(--speakup-red)" }}
                aria-label="Raise a complaint"
            >
                <Plus size={26} />
            </button>
        </Layout>
    );
}

function nextActionLabel(c) {
    switch (c.status) {
        case "drafted":
            return "Review draft and send";
        case "ready_to_send":
        case "awaiting_send":
            return "Send from your email";
        case "sent_unverified":
            return "Add reference number";
        case "awaiting_reply":
            return "Forward the reply when you get one";
        case "escalation_due":
            return "Escalate to next level";
        case "regulator_eligible":
            return "Open RBI Ombudsman co-pilot";
        case "resolved":
            return null;
        default:
            return "Open complaint";
    }
}
